import { useEffect, useState } from "react";

/**
 * A thin gradient bar that pins directly under the sticky nav and fills as the
 * reader scrolls through an article. It doesn't render once the page is fully
 * scrolled past the tracked element.
 *
 * Props:
 *   targetRef  – React ref to the article body element (used to compute progress)
 *   testid     – optional data-testid override
 */
export default function ReadingProgressBar({ targetRef, testid = "reading-progress" }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const compute = () => {
      const el = targetRef?.current;
      if (!el) { setProgress(0); return; }
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      // Total scrollable length of the target relative to the viewport
      const total = rect.height - viewportH * 0.4;
      const scrolled = -rect.top + viewportH * 0.15;
      const p = Math.min(1, Math.max(0, scrolled / Math.max(total, 1)));
      setProgress(p);
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [targetRef]);

  return (
    <div
      data-testid={testid}
      aria-hidden="true"
      className="sticky top-0 z-40 w-full h-[3px] bg-transparent pointer-events-none"
    >
      <div
        data-testid={`${testid}-fill`}
        className="h-full bg-gradient-to-r from-titans-blue via-white to-titans-red transition-[width] duration-100"
        style={{ width: `${Math.round(progress * 100)}%`, willChange: "width" }}
      />
    </div>
  );
}
